/**
 * Transformações geométricas de furos — módulo único compartilhado.
 *
 * Convenção CCW (anti-horária vista de cima), espelho de pose.py/_PROJ:
 *   90°:  XP→YP, XM→YM, YP→XM, YM→XP  |  coord: (L−y, x)
 *   180°: XP→XM, XM→XP, YP→YM, YM→YP  |  coord: (C−x, L−y)
 *   270°: XP→YM, XM→YP, YP→XP, YM→XM  |  coord: (y, C−x)
 *
 * Para flipar para CW: trocar 90↔270 na coord E na tabela de labels, juntos.
 * Sentido físico na mesa Linea pendente de ratificação pelo programador CNC.
 */

export const PROJ = {
  0:   { XP: 'XP', XM: 'XM', YP: 'YP', YM: 'YM' },
  90:  { XP: 'YP', XM: 'YM', YP: 'XM', YM: 'XP' },
  180: { XP: 'XM', XM: 'XP', YP: 'YM', YM: 'YP' },
  270: { XP: 'YM', XM: 'YP', YP: 'XP', YM: 'XM' },
};

/**
 * Aplica rotação CCW em (x, y) usando as dimensões ORIGINAIS da peça.
 * @param {number} x
 * @param {number} y
 * @param {number} graus - 0 | 90 | 180 | 270
 * @param {number} largura - dimensão Y original
 * @param {number} comprimento - dimensão X original
 * @returns {[number, number]} [x_novo, y_novo]
 */
export function rotacionarCoord(x, y, graus, largura, comprimento) {
  if (graus === 90)  return [largura - y, x];
  if (graus === 180) return [comprimento - x, largura - y];
  if (graus === 270) return [y, comprimento - x];
  return [x, y];
}

/**
 * Retorna o label do lado após rotação CCW.
 * @param {string} lado - 'XP' | 'XM' | 'YP' | 'YM'
 * @param {number} graus - 0 | 90 | 180 | 270
 * @returns {string}
 */
export function rotacionarLado(lado, graus) {
  return (PROJ[graus] || {})[lado] || lado;
}

const TOLERANCIA_BORDA_MM = 2.0;

/**
 * Deriva eixo, intervalo ocupado pela broca e borda de entrada de um furo
 * horizontal a partir da GEOMETRIA (x/y vs. dimensões da peça) — nunca do
 * campo `lado` (BM), que não é rótulo confiável de borda (duas convenções
 * reais coexistem entre famílias de produto — ver
 * docs/referencia/formato-mpr/campo-bm-horizontal.md §5/§8). Espelha
 * `geometria_horizontal.extensao_broca_horizontal` do backend.
 *
 * @param {{x: number|string, y: number|string, profundidade: number, lado?: string}} furo
 * @param {number} comprimento
 * @param {number} largura
 * @param {number} [tolerancia=2.0]
 * @returns {{eixo: 'X'|'Y', intervalo: [number, number], borda: 'X0'|'XL'|'Y0'|'YL'}}
 */
export function extensaoBrocaHorizontal(furo, comprimento, largura, tolerancia = TOLERANCIA_BORDA_MM) {
  const x = furo.x === 'x' ? comprimento : Number(furo.x);
  const y = furo.y === 'y' ? largura : Number(furo.y);
  const prof = Number(furo.profundidade);

  if (Math.abs(x - 0) <= tolerancia) {
    return { eixo: 'X', intervalo: [0, prof], borda: 'X0' };
  }
  if (Math.abs(x - comprimento) <= tolerancia) {
    return { eixo: 'X', intervalo: [comprimento - prof, comprimento], borda: 'XL' };
  }
  if (Math.abs(y - 0) <= tolerancia) {
    return { eixo: 'Y', intervalo: [0, prof], borda: 'Y0' };
  }
  if (Math.abs(y - largura) <= tolerancia) {
    return { eixo: 'Y', intervalo: [largura - prof, largura], borda: 'YL' };
  }

  // Fallback — nunca observado na amostra real (ver campo-bm-horizontal.md §3):
  // todo furo horizontal examinado está a ≤2mm de uma borda. Mapeamento
  // atualizado (ciclo 9) pra bater com a regra oficial [CNC] confirmada na
  // bancada (campo-bm-horizontal.md §9.1) -- antes usava um palpite
  // arbitrário (pré-veredito da bancada), hoje reflete a regra confirmada.
  // eslint-disable-next-line no-console
  console.error('furo_horizontal_fora_da_borda_geometrica:', { furo, x, y, comprimento, largura });
  const lado = furo.lado;
  if (lado === 'XP') return { eixo: 'X', intervalo: [0, prof], borda: 'X0' };
  if (lado === 'XM') return { eixo: 'X', intervalo: [comprimento - prof, comprimento], borda: 'XL' };
  if (lado === 'YP') return { eixo: 'Y', intervalo: [0, prof], borda: 'Y0' };
  if (lado === 'YM') return { eixo: 'Y', intervalo: [largura - prof, largura], borda: 'YL' };
  throw new Error(`Furo horizontal fora de qualquer borda e sem lado reconhecido para fallback: x=${x}, y=${y}, lado=${lado}`);
}

const BORDA_OPOSTA = { X0: 'XL', XL: 'X0', Y0: 'YL', YL: 'Y0' };

// Regra OFICIAL confirmada [CNC] na bancada com o programador CNC (WoodWOP
// 6.0.18, 2026-07-24) -- ver docs/referencia/formato-mpr/campo-bm-horizontal.md §9.1.
// Espelha geometria_horizontal.BORDA_PARA_BM_OFICIAL do backend.
const BORDA_PARA_BM_OFICIAL = { X0: 'XP', XL: 'XM', Y0: 'YP', YL: 'YM' };

/**
 * Rotaciona o rótulo `lado` (BM) de um furo horizontal em 180°, validando
 * contra a regra oficial [CNC] (`BORDA_PARA_BM_OFICIAL`) em vez de aprender
 * uma convenção por arquivo (ciclo 7b, DESCONTINUADO no ciclo 9 — a
 * bancada de 2026-07-24 resolveu a pendência: existe uma regra única, não
 * duas convenções coexistindo — ver
 * docs/referencia/formato-mpr/campo-bm-horizontal.md §9). Furo com `lado` divergente
 * da regra oficial é ARQUIVO INVÁLIDO, não uma segunda convenção a
 * preservar — aciona warning explícito, nunca falha silenciosamente.
 *
 * Só cobre 0°/180° (rotação não troca de eixo — X permanece X, Y permanece
 * Y). Para 90°/270° (troca de eixo) ainda delega pra tabela fixa
 * `rotacionarLado`/`PROJ` — gap conhecido (CLAUDE.md, "_PROJ 90°/270° CCW
 * mortos"), não fechado por esta mudança (ver
 * docs/referencia/formato-mpr/campo-bm-horizontal.md §9.4: agora há evidência real de
 * YP/YM, mas destravar o gap fica pra outro ciclo).
 *
 * @param {object} furo - furo horizontal a rotacionar (precisa de `.lado`)
 * @param {number} graus - 0 | 90 | 180 | 270
 * @param {number} comprimento - comprimento da peça no frame original
 * @param {number} largura - largura da peça no frame original
 * @returns {string} novo rótulo `lado`
 */
export function rotacionarLadoAprendido(furo, graus, comprimento, largura) {
  if (graus === 0) return furo.lado;
  if (graus === 90 || graus === 270) {
    return rotacionarLado(furo.lado, graus);
  }

  // graus === 180 -- extensaoBrocaHorizontal propositalmente não é envolvida
  // em try/catch: se o furo estiver fora de qualquer borda (caso patológico),
  // ela já loga (console.error) e levanta; deixamos propagar, não engolimos.
  const { borda } = extensaoBrocaHorizontal(furo, comprimento, largura);

  const esperado = BORDA_PARA_BM_OFICIAL[borda];
  if (furo.lado !== esperado) {
    // eslint-disable-next-line no-console
    console.warn(
      `rotacionarLadoAprendido: BM inválido -- lado=${furo.lado} não bate com a borda ${borda} ` +
      `(esperado ${esperado} pela regra oficial [CNC]) -- arquivo com dado inválido, ` +
      `ver docs/referencia/formato-mpr/campo-bm-horizontal.md §9`,
      { furoUid: furo.uid, borda, lado: furo.lado, esperado }
    );
  }

  return BORDA_PARA_BM_OFICIAL[BORDA_OPOSTA[borda]];
}
